import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import getSiteChartData from "../http/getSiteChartData";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

// Columnar format returned by backend. `fCurtailed` is only emitted when the
// site forecasts non-zero curtailment in year 1.
export interface HalfHourlyChartDataColumnar {
  x: string[];
  fGeneration: number[];
  fDemand: number[];
  fBehindMeterPV: number[];
  fBatteryFromPV: number[];
  fBatteryFromGrid: number[];
  fBatteryFinalCharge: number[];
  fBehindMeterBattery: number[];
  fBehindMeterBatteryGrid: number[];
  fCurtailed?: number[];
  fExportNetwork: number[];
  fExportGrid: number[];
  fImportGrid: number[];
}

// Row format for easier frontend use
export interface HalfHourlyChartDataPoint {
  x: string; // ISO timestamp
  fGeneration: number;
  fDemand: number;
  fBehindMeterPV: number;
  fBatteryFromPV: number;
  fBatteryFromGrid: number;
  fBatteryFinalCharge: number;
  fBehindMeterBattery: number;
  fBehindMeterBatteryGrid: number;
  fCurtailed?: number;
  fExportNetwork: number;
  fExportGrid: number;
  fImportGrid: number;
}

export interface WeatherActualsData {
  x: string[];
  fGenerationInrange: number[];
  fGenerationInrangeByEsys?: Record<string, number[]>;
  fGenerationIdeal?: number[];
  fGenerationIdealByEsys?: Record<string, number[]>;
}

export interface HalfHourlyChartDataResponse {
  siteId: string;
  data: HalfHourlyChartDataPoint[];
  expectedGenerationPeriods?: boolean[];
}

// Columnar format returned by backend
export interface MonthlyChartDataColumnar {
  date: string[];
  actualMetadataFirstDay: (string | null)[];
  actualMetadataLastDay: (string | null)[];
  // P50 Forecast fields - Energy
  fP50Demand: (number | null)[];
  fP50Generation: (number | null)[];
  fP50BehindMeter: (number | null)[];
  fP50GridImport: (number | null)[];
  fP50Exported: (number | null)[];
  fP50NetworkExport: (number | null)[];
  fP50NetworkImport: (number | null)[];
  // P50 Forecast fields - Financial
  fP50LLLeaseRevenue: (number | null)[];
  fP50LLLicenseRevenue: (number | null)[];
  fP50OOLeaseRevenue: (number | null)[];
  fP50OOLicenseRevenue: (number | null)[];
  // P90 Forecast fields - Energy
  fP90Demand: (number | null)[];
  fP90Generation: (number | null)[];
  fP90BehindMeter: (number | null)[];
  fP90Exported: (number | null)[];
  fP90NetworkExport: (number | null)[];
  fP90NetworkImport: (number | null)[];
  // P90 Forecast fields - Financial
  fP90LLLeaseRevenue: (number | null)[];
  fP90LLLicenseRevenue: (number | null)[];
  fP90OOLeaseRevenue: (number | null)[];
  fP90OOLicenseRevenue: (number | null)[];
  // Actual fields - Energy
  aDemand: (number | null)[];
  aGeneration: (number | null)[];
  aBehindMeter: (number | null)[];
  aGridImport: (number | null)[];
  aExported: (number | null)[];
  aNetworkExport: (number | null)[];
  aNetworkImport: (number | null)[];
  aExportedExternalMeasure?: (number | null)[];
  // Actual estimated curtailed kWh per month (null when site has no export
  // limit or when curtailment detection hasn't fully covered the month).
  aCurtailedKwh?: (number | null)[];
  // Actual fields - Financial
  aLLLeaseRevenue: (number | null)[];
  aLLLicenseRevenue: (number | null)[];
  aOOLicenseRevenue: (number | null)[];
  aOOLeaseRevenue: (number | null)[];
  // Invoice fields
  aTariff: (number | null)[];
  aVAT: (number | null)[];
}

// Row format for easier frontend use
export interface MonthlyChartDataPoint {
  date: string; // ISO date string for the month
  actualMetadata?: {
    firstDay: string;
    lastDay: string;
  };
  // P50 Forecast fields - Energy
  fP50Demand?: number;
  fP50Generation?: number;
  fP50BehindMeter?: number;
  fP50GridImport?: number;
  fP50Exported?: number;
  fP50NetworkExport?: number;
  fP50NetworkImport?: number;
  // P50 Forecast fields - Financial
  fP50LLLeaseRevenue?: number;
  fP50LLLicenseRevenue?: number;
  fP50OOLeaseRevenue?: number;
  fP50OOLicenseRevenue?: number;
  // P90 Forecast fields - Energy
  fP90Demand?: number;
  fP90Generation?: number;
  fP90BehindMeter?: number;
  fP90Exported?: number;
  fP90NetworkExport?: number;
  fP90NetworkImport?: number;
  // P90 Forecast fields - Financial
  fP90LLLeaseRevenue?: number;
  fP90LLLicenseRevenue?: number;
  fP90OOLeaseRevenue?: number;
  fP90OOLicenseRevenue?: number;
  // Actual fields - Energy
  aDemand?: number;
  aGeneration?: number;
  aBehindMeter?: number;
  aGridImport?: number;
  aExported?: number;
  aNetworkExport?: number;
  aNetworkImport?: number;
  aExportedExternalMeasure?: number;
  aCurtailedKwh?: number;
  // Actual fields - Financial
  aLLLeaseRevenue?: number;
  aLLLicenseRevenue?: number;
  aOOLicenseRevenue?: number;
  aOOLeaseRevenue?: number;
  // Invoice fields
  aTariff?: number;
  aVAT?: number;
}

export interface MonthlyChartDataResponse {
  siteId: string;
  data: MonthlyChartDataPoint[];
}

// Columnar format for processed readings data
export interface ProcessedReadingsChartDataColumnar {
  x: string[];
  esysUuid: string[];
  demand: number[];
  behindMeterPV: number[];
  behindMeterBattery: number[];
  generation: number[];
  generationSource: string[];
  import: number[];
  importSource: string[];
  export: number[];
  exportSource: string[];
  exportRaw: number[];
  batteryCharge: number[];
  batteryChargeFromGrid: number[];
  batteryDischargeGrid: number[];
  batteryAuxDemand: number[];
  batteryFinalCharge: number[];
  batteryFinalChargeSource: string[];
  exportMetered?: number[];
  networkImport?: number[];
}

// Row format for processed readings data
export interface ProcessedReadingsChartDataPoint {
  x: string;
  esysUuid: string;
  demand: number;
  behindMeterPV: number;
  behindMeterBattery: number;
  generation: number;
  generationSource: string;
  import: number;
  importSource: string;
  export: number;
  exportSource: string;
  exportRaw: number;
  batteryChargeFromPV: number;
  batteryChargeFromGrid: number;
  batteryDischargeGrid: number;
  batteryAuxDemand: number;
  batteryFinalCharge: number;
  batteryFinalChargeSource: string;
  exportMetered?: number;
  networkImport?: number;
}

export interface CurtailmentData {
  x: string[];
  isCurtailed: boolean[];
  isCurtailedSource: string[];
  estimatedCurtailedKwh: (number | null)[];
  estimatedCurtailedKwhSource: string[];
}

export interface ProcessedReadingsChartDataResponse {
  siteId: string;
  start: string;
  end: string;
  data: ProcessedReadingsChartDataPoint[];
  unavailablePeriods?: string[];
  weatherActuals?: WeatherActualsData;
  curtailment?: CurtailmentData;
}

// Types for raw readings data
export interface ReadingVersion {
  ingestionTimestamp: string;
  reading: number;
  source: string;
}

export interface RawReadingsChartHistory {
  esysUuid: string;
  readingTimestamp: string;
  hardwareType: string;
  serialNumber: string;
  readingName: string;
  versions: ReadingVersion[];
}

export interface RawReadingsChartSeries {
  esysUuid: string;
  hardwareType: string;
  serialNumber: string;
  readingName: string;
  data: {
    x: string[];
    reading: number[];
    source: string[];
    ingestionTimestamp: string[];
  };
}

export interface RawReadingsAvailableSeries {
  esysUuid: string;
  hardwareType: string;
  serialNumber: string;
  readingName: string;
}

export interface RawReadingsChartDataResponse {
  siteId: string;
  availableStart: string | null;
  availableEnd: string | null;
  start: string | null;
  end: string | null;
  series: RawReadingsChartSeries[];
  history: RawReadingsChartHistory[];
  truncated: boolean;
  dataPointsLimit: number;
  availableSeries?: RawReadingsAvailableSeries[];
}

// Types for raw state data (non-numeric readings showing state transitions)
// Columnar format for efficient transfer
export interface RawReadingsStateTransitionsColumnar {
  esysUuid: string[];
  hardwareType: string[];
  serialNumber: string[];
  readingName: string[];
  state: string[];
  startTime: string[];
  endTime: (string | null)[];
}

// Row format for frontend consumption
export interface RawReadingsStateTransition {
  esysUuid: string;
  hardwareType: string;
  serialNumber: string;
  readingName: string;
  state: string;
  startTime: string;
  endTime: string | null;
}

export interface RawReadingsStateDataResponse {
  siteId: string;
  availableStart: string | null;
  availableEnd: string | null;
  start: string | null;
  end: string | null;
  stateTransitions: RawReadingsStateTransition[];
  truncated: boolean;
  stateTransitionsLimit: number;
}

export interface PingHistoryDataPoint {
  checkTimestamp: string;
  esysUuid: string;
  pingsSent: number;
  pingsReceived: number;
  packetLossPct: number;
  latencyMsAvg: number | null;
  /** Soracom-reported numeric Mobile Country Code at the time of this
   * check; null when the SIM was offline. */
  networkMcc: number | null;
  /** Soracom-reported numeric Mobile Network Code at the time of this
   * check; null when the SIM was offline. */
  networkMnc: number | null;
  /** Friendly operator name (e.g. "O2 (UK)"); null when the SIM was
   * offline. Unknown (mcc, mnc) pairs fall back to a generic
   * "MCC=NNN MNC=NN" label so the chart always has *some* label per
   * point. */
  networkName: string | null;
}

export interface PingHistoryResponse {
  /** Inclusive YYYY-MM-DD bounds of the returned series, echoed back from the BE. */
  startDate: string;
  endDate: string;
  /** When the request was filtered to one esys, echoes the uuid; null otherwise. */
  esysUuid: string | null;
  series: PingHistoryDataPoint[];
}

// Raw API response with columnar format
interface RawReadingsStateDataApiResponse {
  siteId: string;
  availableStart: string | null;
  availableEnd: string | null;
  start: string | null;
  end: string | null;
  stateTransitions: RawReadingsStateTransitionsColumnar;
  truncated?: boolean;
  stateTransitionsLimit?: number;
}

// Helper function to convert columnar format to row format for half-hourly data
const convertChartDataColumnarToRows = (
  columnar: HalfHourlyChartDataColumnar
): HalfHourlyChartDataPoint[] => {
  const length = columnar.x.length;
  const rows: HalfHourlyChartDataPoint[] = [];

  for (let i = 0; i < length; i++) {
    rows.push({
      x: columnar.x[i],
      fGeneration: columnar.fGeneration[i],
      fDemand: columnar.fDemand[i],
      fBehindMeterPV: columnar.fBehindMeterPV[i],
      fBatteryFromPV: columnar.fBatteryFromPV[i],
      fBatteryFromGrid: columnar.fBatteryFromGrid[i],
      fBatteryFinalCharge: columnar.fBatteryFinalCharge[i],
      fBehindMeterBattery: columnar.fBehindMeterBattery[i],
      fBehindMeterBatteryGrid: columnar.fBehindMeterBatteryGrid[i],
      fCurtailed: columnar.fCurtailed?.[i],
      fExportNetwork: columnar.fExportNetwork[i],
      fExportGrid: columnar.fExportGrid[i],
      fImportGrid: columnar.fImportGrid[i],
    });
  }

  return rows;
};

// Helper function to convert columnar format to row format for monthly data
const convertMonthlyDataColumnarToRows = (
  columnar: MonthlyChartDataColumnar
): MonthlyChartDataPoint[] => {
  const length = columnar.date.length;
  const rows: MonthlyChartDataPoint[] = [];

  for (let i = 0; i < length; i++) {
    const date = columnar.date[i];

    const actualMetadata =
      columnar.actualMetadataFirstDay[i] && columnar.actualMetadataLastDay[i]
        ? {
            firstDay: columnar.actualMetadataFirstDay[i]!,
            lastDay: columnar.actualMetadataLastDay[i]!,
          }
        : undefined;

    rows.push({
      date,
      actualMetadata,
      // P50 Forecast fields - Energy
      fP50Demand: columnar.fP50Demand[i] ?? undefined,
      fP50Generation: columnar.fP50Generation[i] ?? undefined,
      fP50BehindMeter: columnar.fP50BehindMeter[i] ?? undefined,
      fP50GridImport: columnar.fP50GridImport[i] ?? undefined,
      fP50Exported: columnar.fP50Exported[i] ?? undefined,
      fP50NetworkExport: columnar.fP50NetworkExport[i] ?? undefined,
      fP50NetworkImport: columnar.fP50NetworkImport[i] ?? undefined,
      // P50 Forecast fields - Financial
      fP50LLLeaseRevenue: columnar.fP50LLLeaseRevenue[i] ?? undefined,
      fP50LLLicenseRevenue: columnar.fP50LLLicenseRevenue[i] ?? undefined,
      fP50OOLeaseRevenue: columnar.fP50OOLeaseRevenue[i] ?? undefined,
      fP50OOLicenseRevenue: columnar.fP50OOLicenseRevenue[i] ?? undefined,
      // P90 Forecast fields - Energy
      fP90Demand: columnar.fP90Demand[i] ?? undefined,
      fP90Generation: columnar.fP90Generation[i] ?? undefined,
      fP90BehindMeter: columnar.fP90BehindMeter[i] ?? undefined,
      fP90Exported: columnar.fP90Exported[i] ?? undefined,
      fP90NetworkExport: columnar.fP90NetworkExport[i] ?? undefined,
      fP90NetworkImport: columnar.fP90NetworkImport[i] ?? undefined,
      // P90 Forecast fields - Financial
      fP90LLLeaseRevenue: columnar.fP90LLLeaseRevenue[i] ?? undefined,
      fP90LLLicenseRevenue: columnar.fP90LLLicenseRevenue[i] ?? undefined,
      fP90OOLeaseRevenue: columnar.fP90OOLeaseRevenue[i] ?? undefined,
      fP90OOLicenseRevenue: columnar.fP90OOLicenseRevenue[i] ?? undefined,
      // Actual fields - Energy
      aDemand: columnar.aDemand[i] ?? undefined,
      aGeneration: columnar.aGeneration[i] ?? undefined,
      aBehindMeter: columnar.aBehindMeter[i] ?? undefined,
      aGridImport: columnar.aGridImport[i] ?? undefined,
      aExported: columnar.aExported[i] ?? undefined,
      aNetworkExport: columnar.aNetworkExport[i] ?? undefined,
      aNetworkImport: columnar.aNetworkImport[i] ?? undefined,
      aExportedExternalMeasure: columnar.aExportedExternalMeasure?.[i] ?? undefined,
      aCurtailedKwh: columnar.aCurtailedKwh?.[i] ?? undefined,
      // Actual fields - Financial
      aLLLeaseRevenue: columnar.aLLLeaseRevenue[i] ?? undefined,
      aLLLicenseRevenue: columnar.aLLLicenseRevenue[i] ?? undefined,
      aOOLicenseRevenue: columnar.aOOLicenseRevenue[i] ?? undefined,
      aOOLeaseRevenue: columnar.aOOLeaseRevenue[i] ?? undefined,
      // Invoice fields
      aTariff: columnar.aTariff[i] ?? undefined,
      aVAT: columnar.aVAT[i] ?? undefined,
    });
  }

  return rows;
};

// Helper function to convert columnar format to row format for processed readings data
const convertProcessedReadingsDataColumnarToRows = (
  columnar: ProcessedReadingsChartDataColumnar
): ProcessedReadingsChartDataPoint[] => {
  const length = columnar.x.length;
  const rows: ProcessedReadingsChartDataPoint[] = [];

  for (let i = 0; i < length; i++) {
    rows.push({
      x: columnar.x[i],
      esysUuid: columnar.esysUuid[i],
      demand: columnar.demand[i],
      behindMeterPV: columnar.behindMeterPV[i],
      behindMeterBattery: columnar.behindMeterBattery[i],
      generation: columnar.generation[i],
      generationSource: columnar.generationSource[i],
      import: columnar.import[i],
      importSource: columnar.importSource[i],
      export: columnar.export[i],
      exportSource: columnar.exportSource[i],
      exportRaw: columnar.exportRaw[i],
      batteryChargeFromPV: columnar.batteryCharge[i],
      batteryChargeFromGrid: columnar.batteryChargeFromGrid[i],
      batteryDischargeGrid: columnar.batteryDischargeGrid[i],
      batteryAuxDemand: columnar.batteryAuxDemand[i],
      batteryFinalCharge: columnar.batteryFinalCharge[i],
      batteryFinalChargeSource: columnar.batteryFinalChargeSource[i],
      exportMetered: columnar.exportMetered?.[i] ?? 0,
      networkImport: columnar.networkImport?.[i] ?? 0,
    });
  }

  return rows;
};

// Helper function to convert columnar format to row format for state transitions data
const convertStateTransitionsColumnarToRows = (
  columnar: RawReadingsStateTransitionsColumnar
): RawReadingsStateTransition[] => {
  const length = columnar.esysUuid.length;
  const rows: RawReadingsStateTransition[] = [];

  for (let i = 0; i < length; i++) {
    rows.push({
      esysUuid: columnar.esysUuid[i],
      hardwareType: columnar.hardwareType[i],
      serialNumber: columnar.serialNumber[i],
      readingName: columnar.readingName[i],
      state: columnar.state[i],
      startTime: columnar.startTime[i],
      endTime: columnar.endTime[i],
    });
  }

  return rows;
};

const useChartData = ({
  dataType,
  siteId,
  options,
  enabled = true,
}: {
  dataType:
    | "energy_flows"
    | "monthly_forecast_actual"
    | "readings_data_processed"
    | "readings_data_raw"
    | "readings_data_raw_state"
    | "ping_history";
  siteId: string;
  options: object;
  enabled?: boolean;
}): UseQueryResult<
  | HalfHourlyChartDataResponse
  | MonthlyChartDataResponse
  | ProcessedReadingsChartDataResponse
  | RawReadingsChartDataResponse
  | RawReadingsStateDataResponse
  | PingHistoryResponse,
  unknown
> => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  return useQuery(
    ["site", siteId, "chart-data", dataType, options],
    async () => {
      const response = await getSiteChartData(
        await getAuthTokenBlocking(getToken, relogin),
        siteId,
        dataType,
        options
      );

      // Transform columnar format to row format
      if (dataType === "energy_flows") {
        return {
          siteId: response.siteId,
          data: convertChartDataColumnarToRows(
            response.data as HalfHourlyChartDataColumnar
          ),
          expectedGenerationPeriods: response.expectedGenerationPeriods as
            | boolean[]
            | undefined,
        } as HalfHourlyChartDataResponse;
      } else if (dataType === "monthly_forecast_actual") {
        return {
          siteId: response.siteId,
          data: convertMonthlyDataColumnarToRows(
            response.data as MonthlyChartDataColumnar
          ),
        } as MonthlyChartDataResponse;
      } else if (dataType === "readings_data_processed") {
        return {
          siteId: response.siteId,
          start: response.start,
          end: response.end,
          data: convertProcessedReadingsDataColumnarToRows(
            response.data as ProcessedReadingsChartDataColumnar
          ),
          unavailablePeriods: response.unavailablePeriods as string[] | undefined,
          weatherActuals: response.weatherActuals as WeatherActualsData | undefined,
          curtailment: response.curtailment as CurtailmentData | undefined,
        } as ProcessedReadingsChartDataResponse;
      } else if (dataType === "readings_data_raw") {
        // Raw data is already in the correct format from the backend
        return response as RawReadingsChartDataResponse;
      } else if (dataType === "ping_history") {
        // Ping history is already row-shaped from the BE.
        return response as PingHistoryResponse;
      } else if (dataType === "readings_data_raw_state") {
        // Convert columnar format to row format
        const apiResponse = response as RawReadingsStateDataApiResponse;
        return {
          siteId: apiResponse.siteId,
          availableStart: apiResponse.availableStart,
          availableEnd: apiResponse.availableEnd,
          start: apiResponse.start,
          end: apiResponse.end,
          stateTransitions: convertStateTransitionsColumnarToRows(
            apiResponse.stateTransitions
          ),
          truncated: apiResponse.truncated ?? false,
          stateTransitionsLimit: apiResponse.stateTransitionsLimit ?? 10000,
        } as RawReadingsStateDataResponse;
      }

      return response;
    },
    {
      enabled: enabled && !!siteId,
      // Cache for 5 minutes since this data is relatively stable
      staleTime: 5 * 60 * 1000,
    }
  );
};

export default useChartData;
